/**
 * Shared section analytics hook
 * Provides section_rendered and section_interacted tracking
 * Can be used with static sections (NoteSection) or dynamic sections based on headings (JotDoc)
 */

import { useCallback,useEffect, useRef } from 'react';

import { buildSectionInteracted, buildSectionRendered, type TelemetryContext } from '../../utils/analytics/noteTelemetryBuilder';

export type SectionInfo = {
  id: string;
  title: string;
};

export type GetActiveSectionFn = (element: HTMLElement | null) => SectionInfo | null;

export type SectionAnalyticsOptions = {
  context: TelemetryContext;
  getActiveSection: GetActiveSectionFn | SectionInfo; // Static section or function to get section from element
  thresholdMs?: number; // Hover duration threshold (default 2000ms)
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
    section_title?: string;
    encounter_id?: string;
  };
  onSectionRendered?: (sectionId: string) => void;
  onSectionInteracted?: (sectionId: string, duration: number) => void;
};

/**
 * Hook to track section rendered events using IntersectionObserver
 */
export function useSectionRendered(
  containerRef: React.RefObject<HTMLElement>,
  opts: SectionAnalyticsOptions,
) {
  const renderedSectionsRef = useRef<Set<string>>(new Set());
  const isStaticSection = typeof opts.getActiveSection === 'object' && 'id' in opts.getActiveSection;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Use IntersectionObserver so events only fire after element enters viewport
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting || !entry.target) return;

          // Determine section info
          let section: SectionInfo | null = null;
          if (isStaticSection) {
            section = opts.getActiveSection as SectionInfo;
          } else {
            const getSection = opts.getActiveSection as GetActiveSectionFn;
            section = getSection(entry.target as HTMLElement);
          }

          if (section && !renderedSectionsRef.current.has(section.id)) {
            renderedSectionsRef.current.add(section.id);
            buildSectionRendered({
              context: opts.context,
              metadata: {
                ...opts.metadata,
                section_id: section.id,
                section_title: section.title,
              },
            });
            opts.onSectionRendered?.(section.id);
          }
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, opts, isStaticSection]);
}

/**
 * Hook to track section interacted events (hover with threshold)
 * Tracks clicks, selects, and copies within sections
 */
export function useSectionInteracted(
  containerRef: React.RefObject<HTMLElement>,
  opts: SectionAnalyticsOptions,
) {
  const thresholdMs = opts.thresholdMs ?? 2000; // 2 seconds default
  const currentHoveredSectionRef = useRef<string | null>(null);
  const hoverStartTimeRef = useRef<number | null>(null);
  const sectionInteractionCountsRef = useRef<
    Map<string, { clicks: number; selects: number; copies: number }>
  >(new Map());
  const isStaticSection = typeof opts.getActiveSection === 'object' && 'id' in opts.getActiveSection;

  // Get section for an element
  const getSectionForElement = useCallback(
    (element: HTMLElement | null): SectionInfo | null => {
      if (!element) return null;
      if (isStaticSection) {
        return opts.getActiveSection as SectionInfo;
      }
      const getSection = opts.getActiveSection as GetActiveSectionFn;
      return getSection(element);
    },
    [opts.getActiveSection, isStaticSection],
  );

  // Handle mouse enter (for static sections) or mouse move (for dynamic sections)
  const handleMouseEnter = useCallback(() => {
    const section = isStaticSection
      ? (opts.getActiveSection as SectionInfo)
      : getSectionForElement(containerRef.current);
    if (!section) return;

    currentHoveredSectionRef.current = section.id;
    hoverStartTimeRef.current = performance.now();
    sectionInteractionCountsRef.current.set(section.id, { clicks: 0, selects: 0, copies: 0 });
  }, [containerRef, getSectionForElement, opts, isStaticSection]);

  // Handle mouse move to detect section transitions (for dynamic sections)
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isStaticSection) return; // Static sections use mouse enter/leave only

      const target = e.target as HTMLElement | null;
      if (!target || !containerRef.current?.contains(target)) return;

      const section = getSectionForElement(target);
      if (!section) return;

      const sectionId = section.id;

      // If hovering a new section, check threshold for previous section
      if (currentHoveredSectionRef.current !== sectionId) {
        if (currentHoveredSectionRef.current && hoverStartTimeRef.current !== null) {
          const duration = performance.now() - hoverStartTimeRef.current;

          if (duration >= thresholdMs) {
            const counts = sectionInteractionCountsRef.current.get(currentHoveredSectionRef.current) || {
              clicks: 0,
              selects: 0,
              copies: 0,
            };

            // Get section title from the getSectionForElement function
            const prevSectionElement = containerRef.current?.querySelector(`[data-section-id="${currentHoveredSectionRef.current}"]`);
            const prevSection = prevSectionElement ? getSectionForElement(prevSectionElement as HTMLElement) : null;

            buildSectionInteracted({
              context: opts.context,
              hoverDuration: duration,
              interactionCounts: counts,
              metadata: {
                ...opts.metadata,
                section_id: currentHoveredSectionRef.current,
                section_title: prevSection?.title,
              },
            });

            opts.onSectionInteracted?.(currentHoveredSectionRef.current, duration);
            sectionInteractionCountsRef.current.delete(currentHoveredSectionRef.current);
          }
        }

        // Start tracking new section
        currentHoveredSectionRef.current = sectionId;
        hoverStartTimeRef.current = performance.now();
        sectionInteractionCountsRef.current.set(sectionId, { clicks: 0, selects: 0, copies: 0 });
      }
    },
    [containerRef, getSectionForElement, opts, thresholdMs, isStaticSection],
  );

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (currentHoveredSectionRef.current && hoverStartTimeRef.current !== null) {
      const duration = performance.now() - hoverStartTimeRef.current;

      if (duration >= thresholdMs) {
        const counts = sectionInteractionCountsRef.current.get(currentHoveredSectionRef.current) || {
          clicks: 0,
          selects: 0,
          copies: 0,
        };

        const section = isStaticSection
          ? (opts.getActiveSection as SectionInfo)
          : getSectionForElement(containerRef.current);

        buildSectionInteracted({
          context: opts.context,
          hoverDuration: duration,
          interactionCounts: counts,
          metadata: {
            ...opts.metadata,
            section_id: currentHoveredSectionRef.current,
            section_title: section?.title,
          },
        });

        opts.onSectionInteracted?.(currentHoveredSectionRef.current, duration);
        sectionInteractionCountsRef.current.delete(currentHoveredSectionRef.current);
      }
    }
    currentHoveredSectionRef.current = null;
    hoverStartTimeRef.current = null;
  }, [containerRef, getSectionForElement, opts, thresholdMs, isStaticSection]);

  // Track clicks
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = () => {
      if (currentHoveredSectionRef.current) {
        const counts = sectionInteractionCountsRef.current.get(currentHoveredSectionRef.current);
        if (counts) {
          counts.clicks += 1;
        }
      }
    };

    container.addEventListener('click', handleClick);
    return () => {
      container.removeEventListener('click', handleClick);
    };
  }, [containerRef]);

  // Track selects and copies (expose helpers for external tracking)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const trackSelect = (sectionId: string) => {
      const counts = sectionInteractionCountsRef.current.get(sectionId);
      if (counts) {
        counts.selects += 1;
      }
    };

    const trackCopy = (sectionId: string) => {
      const counts = sectionInteractionCountsRef.current.get(sectionId);
      if (counts) {
        counts.copies += 1;
      }
    };

    const handleCopy = () => {
      if (currentHoveredSectionRef.current) {
        trackCopy(currentHoveredSectionRef.current);
      }
    };

    // Expose helpers for external use (e.g., from selection analytics hooks)
    (container as any).__sectionAnalytics = {
      trackTextSelected: trackSelect,
      trackTextCopied: trackCopy,
      getSectionIdForElement: (element: HTMLElement | null) => {
        const section = getSectionForElement(element);
        return section?.id || null;
      },
    };

    container.addEventListener('copy', handleCopy);
    document.addEventListener('copy', handleCopy); // For BlockNote

    return () => {
      delete (container as any).__sectionAnalytics;
      container.removeEventListener('copy', handleCopy);
      document.removeEventListener('copy', handleCopy);
    };
  }, [containerRef, getSectionForElement]);

  // Mouse enter/move and leave handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (isStaticSection) {
      // For static sections, use mouse enter/leave
      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);
    } else {
      // For dynamic sections, use mouse move/leave
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (isStaticSection) {
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
      } else {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [containerRef, handleMouseEnter, handleMouseMove, handleMouseLeave, isStaticSection]);
}

